import React, { useState, useRef, useEffect } from 'react';
import { Icon } from '@iconify/react';

export interface ColorOption {
  name: string;
  hex: string;
}

export const JERSEY_COLORS: ColorOption[] = [
  { name: 'Merah', hex: '#E24C3F' },
  { name: 'Oranye', hex: '#FC480D' },
  { name: 'Kuning', hex: '#F4B740' },
  { name: 'Hijau', hex: '#3FA85C' },
  { name: 'Tosca', hex: '#2FB6A6' },
  { name: 'Biru', hex: '#3B7DDB' },
  { name: 'Navy', hex: '#1F3A6E' },
  { name: 'Ungu', hex: '#7A5AC7' },
  { name: 'Pink', hex: '#E15B9E' },
  { name: 'Hitam', hex: '#222226' },
  { name: 'Abu-abu', hex: '#8A8F98' },
  { name: 'Putih', hex: '#FFFFFF' },
];

interface SparringFormProps {
  isSparring: boolean;
  onToggleSparring: (checked: boolean) => void;
  teamAName: string;
  onTeamANameChange?: (name: string) => void;
  teamAColor: string;
  onTeamAColorChange: (hex: string) => void;
  teamBName: string;
  onTeamBNameChange: (name: string) => void;
  teamBColor: string;
  onTeamBColorChange: (hex: string) => void;
  embedded?: boolean;
}

const getJerseyButtonStyle = (hexColor: string) => {
  const isWhite = !hexColor || hexColor.toUpperCase() === '#FFFFFF';
  if (isWhite) {
    return {
      color: '#374151',
      backgroundColor: '#FFFFFF',
      borderColor: '#D1D5DB',
    };
  }
  return {
    color: hexColor,
    backgroundColor: `${hexColor}1A`,
    borderColor: hexColor,
  };
};

const SparringForm: React.FC<SparringFormProps> = ({
  isSparring,
  onToggleSparring,
  teamAName,
  onTeamANameChange,
  teamAColor,
  onTeamAColorChange,
  teamBName,
  onTeamBNameChange,
  teamBColor,
  onTeamBColorChange,
  embedded = false,
}) => {
  const [openPopover, setOpenPopover] = useState<'A' | 'B' | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpenPopover(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const colorA = JERSEY_COLORS.find((c) => c.hex.toLowerCase() === teamAColor.toLowerCase());
  const colorB = JERSEY_COLORS.find((c) => c.hex.toLowerCase() === teamBColor.toLowerCase());

  return (
    <div
      ref={containerRef}
      className={
        embedded
          ? 'space-y-4'
          : 'bg-white rounded-2xl border border-gray-200/80 p-5 sm:p-6 space-y-4 shadow-xs'
      }
    >
      {/* Toggle Row */}
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h3 className="text-sm font-bold text-gray-900">Mode Sparring</h3>
          <p className="text-xs text-gray-500 leading-relaxed max-w-lg">
            Jadikan sesi ini tanding antar dua tim/komunitas. Tim kamu dan tim lawan sama-sama pakai satu open list lewat link yang sama.
          </p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer flex-shrink-0 mt-0.5">
          <input
            type="checkbox"
            checked={isSparring}
            onChange={(e) => onToggleSparring(e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FC480D]"></div>
        </label>
      </div>

      {/* Sparring Panel */}
      {isSparring && (
        <div className="pt-2 space-y-3">
          <div className="relative flex flex-col items-center">
            {/* TIM A */}
            <div
              className="w-full bg-white border-2 rounded-2xl p-4 relative transition-all"
              style={{
                borderColor: teamAColor || '#FC480D',
              }}
            >
              <div className="text-[11px] font-bold tracking-wider uppercase mb-1.5 text-[#FC480D]">
                TIM A · KAMU
              </div>
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <input
                    type="text"
                    value={teamAName}
                    onChange={(e) => onTeamANameChange?.(e.target.value)}
                    placeholder="Nama Komunitas Kita"
                    className="w-full bg-transparent border-none text-base sm:text-lg font-bold text-gray-900 p-0 focus:outline-none placeholder:text-gray-400 placeholder:font-normal"
                  />
                  {colorA && (
                    <div className="flex items-center gap-1.5 mt-1.5 text-xs text-gray-600 font-medium">
                      <span
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{
                          backgroundColor: colorA.hex,
                          boxShadow: colorA.hex === '#FFFFFF' ? 'inset 0 0 0 1px #DADCE0' : 'none',
                        }}
                      />
                      <span>{colorA.name}</span>
                    </div>
                  )}
                </div>

                {/* Jersey Button Team A */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenPopover(openPopover === 'A' ? null : 'A');
                    }}
                    className="w-10 h-10 rounded-xl border flex items-center justify-center flex-shrink-0 transition-all hover:opacity-90 shadow-2xs cursor-pointer"
                    style={getJerseyButtonStyle(teamAColor)}
                    title="Pilih warna jersey Tim A"
                  >
                    <Icon icon="ph:t-shirt-bold" className="w-5 h-5" />
                  </button>

                  {/* Popover Swatch Grid Team A */}
                  {openPopover === 'A' && (
                    <div className="absolute top-[calc(100%+8px)] right-0 bg-white border border-gray-200 rounded-xl shadow-xl p-3 z-30 w-48 animate-in fade-in duration-150">
                      <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                        Warna Jersey Tim A
                      </div>
                      <div className="grid grid-cols-5 gap-2">
                        {JERSEY_COLORS.map((c) => (
                          <button
                            key={c.hex}
                            type="button"
                            title={c.name}
                            onClick={() => {
                              onTeamAColorChange(c.hex);
                              setOpenPopover(null);
                            }}
                            className={`w-7 h-7 rounded-full border-2 border-white shadow-xs flex items-center justify-center relative transition-transform hover:scale-110 cursor-pointer ${
                              teamAColor.toLowerCase() === c.hex.toLowerCase()
                                ? 'ring-2 ring-[#FC480D] scale-105'
                                : 'ring-1 ring-gray-300'
                            }`}
                            style={{ backgroundColor: c.hex }}
                          >
                            {teamAColor.toLowerCase() === c.hex.toLowerCase() && (
                              <Icon
                                icon="ph:check-bold"
                                className={`w-3.5 h-3.5 font-bold ${
                                  c.hex === '#FFFFFF' ? 'text-gray-800' : 'text-white'
                                }`}
                              />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* VS Badge */}
            <div className="w-8 h-8 rounded-full bg-[#FC480D] text-white font-extrabold text-xs flex items-center justify-center shadow-md z-10 -my-2.5 border-2 border-white">
              VS
            </div>

            {/* TIM B */}
            <div
              className="w-full bg-gray-50/70 border border-gray-200 rounded-2xl p-4 relative transition-all"
              style={{
                borderColor: openPopover === 'B' ? teamBColor || '#3B7DDB' : '#E5E7EB',
              }}
            >
              <div className="text-[11px] font-bold tracking-wider uppercase mb-1.5 text-gray-400">
                TIM B · LAWAN
              </div>
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <input
                    type="text"
                    value={teamBName}
                    onChange={(e) => onTeamBNameChange(e.target.value)}
                    placeholder="Nama Komunitas Lawan"
                    className="w-full bg-transparent border-none text-base sm:text-lg font-bold text-gray-900 p-0 focus:outline-none placeholder:text-gray-400 placeholder:font-normal"
                  />
                  {colorB && (
                    <div className="flex items-center gap-1.5 mt-1.5 text-xs text-gray-600 font-medium">
                      <span
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{
                          backgroundColor: colorB.hex,
                          boxShadow: colorB.hex === '#FFFFFF' ? 'inset 0 0 0 1px #DADCE0' : 'none',
                        }}
                      />
                      <span>{colorB.name}</span>
                    </div>
                  )}
                </div>

                {/* Jersey Button Team B */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenPopover(openPopover === 'B' ? null : 'B');
                    }}
                    className="w-10 h-10 rounded-xl border flex items-center justify-center flex-shrink-0 transition-all hover:opacity-90 shadow-2xs cursor-pointer"
                    style={getJerseyButtonStyle(teamBColor)}
                    title="Pilih warna jersey Tim B"
                  >
                    <Icon icon="ph:t-shirt-bold" className="w-5 h-5" />
                  </button>

                  {/* Popover Swatch Grid Team B */}
                  {openPopover === 'B' && (
                    <div className="absolute top-[calc(100%+8px)] right-0 bg-white border border-gray-200 rounded-xl shadow-xl p-3 z-30 w-48 animate-in fade-in duration-150">
                      <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                        Warna Jersey Tim B
                      </div>
                      <div className="grid grid-cols-5 gap-2">
                        {JERSEY_COLORS.map((c) => (
                          <button
                            key={c.hex}
                            type="button"
                            title={c.name}
                            onClick={() => {
                              onTeamBColorChange(c.hex);
                              setOpenPopover(null);
                            }}
                            className={`w-7 h-7 rounded-full border-2 border-white shadow-xs flex items-center justify-center relative transition-transform hover:scale-110 cursor-pointer ${
                              teamBColor.toLowerCase() === c.hex.toLowerCase()
                                ? 'ring-2 ring-[#FC480D] scale-105'
                                : 'ring-1 ring-gray-300'
                            }`}
                            style={{ backgroundColor: c.hex }}
                          >
                            {teamBColor.toLowerCase() === c.hex.toLowerCase() && (
                              <Icon
                                icon="ph:check-bold"
                                className={`w-3.5 h-3.5 font-bold ${
                                  c.hex === '#FFFFFF' ? 'text-gray-800' : 'text-white'
                                }`}
                              />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-500 leading-relaxed pt-1">
            Nama Tim B bisa diatur sesuai kesepakatan. Warna jersey membantu wasit &amp; peserta membedakan tim saat input skor.
          </p>
        </div>
      )}
    </div>
  );
};

export default SparringForm;
